import React, { useState, useEffect, useCallback } from "react";
import T from '../../components/common/T';
import { useParams, useNavigate } from "react-router-dom";
import api from "../../api/axiosConfig";
import OdooFormLayout from "../../components/OdooFormLayout"; 
import RecordBillPaymentModal from "./RecordBillPaymentModal"; 
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ClockIcon, BanknotesIcon } from "@heroicons/react/24/outline";

// 🟢 IMPORT GLOBAL CURRENCY HOOK
import { useCurrency } from '../../hooks/useCurrency';

export default function VendorBillDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // 🟢 PULL FORMATTER
  const { formatMoney } = useCurrency();
  
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPayModal, setShowPayModal] = useState(false);

  const fetchBill = useCallback(async () => {
    try {
      const res = await api.get(`/accounting/bills/${id}`);
      setBill(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load bill details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchBill();
  }, [fetchBill]);

  if (loading) return <div className="p-10 text-center text-gray-500"><T>Loading Bill...</T></div>;
  if (!bill) return <div className="p-10 text-center text-red-500"><T>Bill not found</T></div>;

  const ActionButtons = (
    <>
      {bill.status !== "Paid" && (
        <button 
          onClick={() => setShowPayModal(true)}
          className="bg-teal-600 text-white px-4 py-1.5 rounded text-xs font-bold uppercase shadow-sm hover:bg-teal-700 flex items-center gap-2 transition"
        >
          <BanknotesIcon className="w-4 h-4" /> Register Payment
        </button>
      )}
      <button 
        onClick={() => navigate(-1)} 
        className="bg-white dark:bg-[#21262d] border border-gray-300 dark:border-[#30363d] text-gray-700 dark:text-slate-300 px-3 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-slate-800 text-xs font-bold uppercase ml-2 transition"
      >
        Back
      </button>
    </>
  );

  return (
    <>
      
      <OdooFormLayout
        title={`BILL / ${bill.bill_number}`}
        subTitle={`Vendor: ${bill.vendor_name}`}
        status={bill.status}
        stages={["Draft", "Posted", "Paid"]}
        actions={ActionButtons}
      >
        {bill.status === "Paid" && (
            <div className="absolute top-0 right-0 w-24 h-24 overflow-hidden z-20 pointer-events-none">
                <div className="absolute top-3 -right-8 w-32 bg-green-500 text-white text-xs font-bold py-1.5 text-center transform rotate-45 shadow-md">
                    PAID
                </div>
            </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 mb-8">
            <div className="space-y-4">
                <div className="flex items-center">
                    <label className="w-32 text-xs font-bold text-gray-700 dark:text-slate-400 uppercase"><T>Vendor</T></label> 
                    <span className="border-b border-gray-200 dark:border-[#30363d] flex-1 py-1 text-gray-900 dark:text-white font-medium">{bill.vendor_name}</span>
                </div>
                <div className="flex items-center">
                    <label className="w-32 text-xs font-bold text-gray-700 dark:text-slate-400 uppercase"><T>Project</T></label> 
                    <span className="border-b border-gray-200 dark:border-[#30363d] flex-1 py-1 text-indigo-600 dark:text-indigo-400 font-medium">
                        {bill.project_name || "General Expense"}
                    </span>
                </div>
                {bill.notes && (
                    <div className="flex items-start mt-2">
                        <label className="w-32 text-xs font-bold text-gray-700 dark:text-slate-400 uppercase mt-1"><T>Reference</T></label> 
                        <span className="bg-yellow-50 dark:bg-amber-950/40 text-yellow-800 dark:text-amber-300 text-xs px-2 py-1 rounded border border-yellow-200 dark:border-amber-800/50 flex-1 italic">
                            {bill.notes}
                        </span>
                    </div>
                )}
            </div>

            <div className="space-y-4">
                <div className="flex items-center">
                    <label className="w-32 text-xs font-bold text-gray-700 dark:text-slate-400 uppercase"><T>Bill Date</T></label> 
                    <span className="border-b border-gray-200 dark:border-[#30363d] flex-1 py-1 font-medium text-gray-800 dark:text-slate-200">{new Date(bill.bill_date).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center">
                    <label className="w-32 text-xs font-bold text-gray-700 dark:text-slate-400 uppercase"><T>Due Date</T></label> 
                    <span className={`border-b border-gray-200 dark:border-[#30363d] flex-1 py-1 font-bold ${new Date(bill.due_date) < new Date() && bill.status !== 'Paid' ? 'text-red-600 dark:text-rose-400' : 'text-gray-900 dark:text-white'}`}>
                        {new Date(bill.due_date).toLocaleDateString()}
                    </span>
                </div>
            </div>
        </div>

        <div className="bg-gray-50 dark:bg-[#21262d] rounded-xl p-6 border border-gray-200 dark:border-[#30363d] mb-8 transition-colors">
            <div className="flex justify-end">
                <div className="w-72 space-y-2">
                    <div className="flex justify-between text-sm text-gray-600 dark:text-slate-400">
                        <span><T>Total Amount:</T></span>
                        {/* 🟢 REPLACED <Price> with formatMoney */}
                        <span className="font-bold text-gray-900 dark:text-white">{formatMoney(Number(bill.total_amount))}</span>
                    </div>
                    <div className="flex justify-between text-sm text-green-600 dark:text-emerald-400">
                        <span><T>Paid Amount:</T></span>
                        <span>- {formatMoney(Number(bill.paid_amount || 0))}</span>
                    </div>
                    <div className="flex justify-between text-lg font-black text-gray-900 dark:text-white border-t border-gray-300 dark:border-[#30363d] pt-2 mt-2">
                        <span><T>Balance Due:</T></span>
                        <span className={Number(bill.balance_due) > 0 ? "text-red-600 dark:text-rose-400" : "text-green-600 dark:text-emerald-400"}>
                            {formatMoney(Number(bill.balance_due))}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <div>
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200 dark:border-[#30363d]">
                <ClockIcon className="w-5 h-5 text-gray-500 dark:text-slate-400" />
                <h3 className="text-base font-bold text-gray-800 dark:text-white uppercase tracking-wide"><T>Payment History</T></h3>
            </div>

            {(!bill.payments || bill.payments.length === 0) ? (
                <div className="text-center py-8 bg-gray-50 dark:bg-[#21262d] rounded-lg border border-dashed border-gray-300 dark:border-[#30363d] text-gray-400 dark:text-slate-400 text-sm">
                    No payments recorded yet.
                </div>
            ) : (
                <div className="bg-white dark:bg-[#161b22] rounded-lg border border-gray-200 dark:border-[#30363d] overflow-hidden transition-colors">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 dark:bg-[#21262d] text-gray-500 dark:text-slate-400 font-bold border-b border-gray-200 dark:border-[#30363d]">
                            <tr>
                                <th className="px-4 py-3"><T>Date</T></th>
                                <th className="px-4 py-3"><T>Method</T></th>
                                <th className="px-4 py-3"><T>Reference</T></th>
                                <th className="px-4 py-3 text-right"><T>Amount</T></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-[#30363d]">
                            {bill.payments.map((pay, i) => (
                                <tr key={i} className="hover:bg-gray-50 dark:hover:bg-slate-800/50">
                                    <td className="px-4 py-3 text-gray-800 dark:text-slate-200">{new Date(pay.payment_date).toLocaleDateString()}</td>
                                    <td className="px-4 py-3 capitalize text-gray-800 dark:text-slate-200">{pay.payment_method.replace('_', ' ')}</td>
                                    <td className="px-4 py-3 font-mono text-xs text-gray-600 dark:text-slate-400">{pay.reference || '-'}</td>
                                    {/* 🟢 REPLACED <Price> with formatMoney */}
                                    <td className="px-4 py-3 text-right font-bold text-green-700 dark:text-emerald-400">{formatMoney(Number(pay.amount))}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>

      </OdooFormLayout>

      <RecordBillPaymentModal 
        isOpen={showPayModal}
        onClose={() => setShowPayModal(false)}
        bill={bill}
        onSuccess={fetchBill} 
      />
    </>
  );
}